import React from 'react';
import { Autocomplete } from '@react-google-maps/api';
import {
  Box,
  Typography,
  Button,
  Slider,
  CircularProgress,
  FormControl,
  Drawer,
  Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MapIcon from '@mui/icons-material/Map';
import {
  ButtonTertiary,
  ButtonTertiaryIcon,
  GlassColors,
  GlassTextField,
  NeumorphicButton,
} from '../../../components/glassmorphism/GlassmorphismStyles';
import CategoryFilterNew from './CategoryFilterNew';
import { ReactComponent as CloseIcon } from '../../../assets/icons/nav/close.svg';

type Props = {
  filtersOpen: boolean;
  isMobile: boolean;
  isDark: boolean;

  setMobileFiltersOpen: (v: boolean) => void;

  isCategoriesFilterVisible: boolean;
  isLocationFilterVisible: boolean;
  isNameFilterVisible: boolean;

  // categories
  isFetchingCategories: boolean;
  categoriesError: string | null;
  transformedCategories: any[];
  selectedCategoryKeys: Set<string>;
  toggleCategory: (key: string) => void;
  addAllSubcategoryKeys: (keys: string[]) => void;
  removeAllSubcategoryKeys: (keys: string[]) => void;
  getAllCategories: () => void;

  // location
  autocompleteRef: React.MutableRefObject<google.maps.places.Autocomplete | null>;
  selectedAddress: string | null;
  addressInputValue: string;
  setAddressInputValue: (v: string) => void;
  setSelectedAddress: (v: string | null) => void;
  setSelectedCoordinates: (
    coords: { lat: number; lng: number } | null,
  ) => void;

  address?: string | null; // order address
  handleUseOrderAddress: () => void;

  radiusKm: number | null;
  setRadiusKm: (v: number | null) => void;
  setAppliedRadiusKm: (v: number | null) => void;

  handleOpenMapPopup: () => void;

  // handyman details
  handymanSearchText: string;
  handleHandymanSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  hourlyRateRange: [number, number];
  handleHourlyRateRangeChange: (
    event: Event,
    newValue: number | number[],
  ) => void;

  fixedPriceRange: [number, number];
  handleFixedPriceRangeChange: (
    event: Event,
    newValue: number | number[],
  ) => void;

  // reset
  handleResetFilters: () => void;
  hasActiveFilters: boolean;
};

export default function BrowseHandymanFilters(props: Props) {
  return (
    <>
      {props.isMobile ? (
        <Drawer
          anchor='bottom'
          open={props.filtersOpen}
          onClose={() => props.setMobileFiltersOpen(false)}
          sx={{ zIndex: 999999 }}
          PaperProps={{
            sx: {
              height: '100dvh',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              backgroundImage: 'none',
              backgroundColor: props.isDark
                ? 'rgba(26, 26, 26, 1)'
                : 'rgba(229, 229, 220, 1)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              p: '16px',
              gap: '24px',
            }}
          >
            {/* Filter Actions Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '24px',
                width: '100%',
                position: 'relative',
              }}
            >
              {/* Title */}
              <Box
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '24px',
                  lineHeight: '133%',
                  color: props.isDark ? '#00FFFF' : '#005CAD',
                }}
              >
                Filters
              </Box>
              {props.isMobile && (
                <ButtonTertiaryIcon
                  onClick={() => props.setMobileFiltersOpen(false)}
                  sx={{
                    position: 'absolute',
                    zIndex: 1,
                    width: '32px',
                    height: '32px',
                    top: 0,
                    right: 0,
                    '& svg': {
                      fontSize: '16px',
                      '& path': {
                        fill: '#E2F7FF',
                      },
                    },
                  }}
                >
                  <CloseIcon />
                </ButtonTertiaryIcon>
              )}
            </Box>

            {/* Categories Section */}
            {props.isCategoriesFilterVisible && (
              <>
                {props.isFetchingCategories && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      my: 2,
                    }}
                  >
                    <CircularProgress size={24} sx={{ color: '#00FFFF' }} />
                  </Box>
                )}

                {props.categoriesError && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        mb: 1,
                        fontFamily: 'Roboto',
                        fontSize: '14px',
                        color: '#F44336',
                      }}
                    >
                      {props.categoriesError}
                    </Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => props.getAllCategories()}
                      sx={{ textTransform: 'none' }}
                    >
                      Retry
                    </Button>
                  </Box>
                )}

                {!props.isFetchingCategories &&
                  !props.categoriesError &&
                  props.transformedCategories.length === 0 && (
                    <Typography
                      sx={{
                        mb: 2,
                        fontFamily: 'Roboto',
                        fontSize: '14px',
                        color: props.isDark ? '#D5D5D5' : '#585858',
                      }}
                    >
                      No categories available
                    </Typography>
                  )}

                {props.transformedCategories.length > 0 && (
                  <CategoryFilterNew
                    categories={props.transformedCategories}
                    selectedCategoryKeys={props.selectedCategoryKeys}
                    toggleCategory={props.toggleCategory}
                    addAllSubcategoryKeys={props.addAllSubcategoryKeys}
                    removeAllSubcategoryKeys={props.removeAllSubcategoryKeys}
                  />
                )}
              </>
            )}

            {/* Location Filter */}
            {props.isLocationFilterVisible && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '24px',
                  width: '100%',
                  '& > div': {
                    width: '100%',
                  },
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Montserrat',
                    fontStyle: 'normal',
                    fontWeight: 800,
                    fontSize: '20px',
                    lineHeight: '160%',
                    letterSpacing: '0.15px',
                    color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Location
                </Box>

                <Autocomplete
                  onLoad={(autocomplete) => {
                    props.autocompleteRef.current = autocomplete;
                  }}
                  onPlaceChanged={() => {
                    const place = props.autocompleteRef.current?.getPlace();
                    if (place?.formatted_address) {
                      props.setAddressInputValue(place.formatted_address);
                      props.setSelectedAddress(place.formatted_address);
                      const lat = place.geometry?.location?.lat();
                      const lng = place.geometry?.location?.lng();
                      if (typeof lat === 'number' && typeof lng === 'number') {
                        props.setSelectedCoordinates({ lat, lng });
                      } else {
                        props.setSelectedCoordinates(null);
                      }
                    }
                  }}
                >
                  <GlassTextField
                    placeholder={`${props.selectedAddress || ''} Search Address`}
                    fullWidth
                    value={props.addressInputValue}
                    onChange={(e) => {
                      props.setAddressInputValue(e.target.value);
                      // Freeform typing invalidates previously-picked coordinates.
                      props.setSelectedCoordinates(null);
                    }}
                    sx={{
                      '& input::placeholder': {
                        color: props.isDark ? '#B0B0B0' : '#1A1A1A',
                        opacity: 1,
                      },
                    }}
                  />
                </Autocomplete>

                {/* Use Order Address Button */}
                {props.address && (
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={props.handleUseOrderAddress}
                    sx={{
                      mt: 1,
                      width: '100%',
                      fontFamily: 'Montserrat',
                      fontSize: '12px',
                      textTransform: 'none',
                      borderColor: props.isDark
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)',
                      color: props.isDark ? '#D5D5D5' : '#1A1A1A',
                      '&:hover': {
                        borderColor: props.isDark
                          ? 'rgba(255, 255, 255, 0.5)'
                          : 'rgba(0, 0, 0, 0.5)',
                        backgroundColor: props.isDark
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    Use Order Address
                  </Button>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '143%',
                      letterSpacing: '0.17px',
                      color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    Search Radius (miles)
                  </Box>

                  <Box sx={{ width: '100%', px: '12px' }}>
                    <Slider
                      value={props.radiusKm ?? 10}
                      onChange={(e, value) =>
                        props.setRadiusKm(value as number)
                      }
                      onChangeCommitted={(e, value) => {
                        props.setAppliedRadiusKm(value as number);
                      }}
                      min={1}
                      max={200}
                      step={1}
                      valueLabelDisplay='auto'
                      sx={{
                        width: '100%',
                        height: '8px',
                        padding: '0px',
                        '& .MuiSlider-track': {
                          background: '#005CAD',
                          boxShadow: `
                            0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054
                          `,
                          borderRadius: '100px',
                          border: 'none',
                        },
                        '& .MuiSlider-rail': {
                          background: props.isDark ? '#1A1A1A' : '#E5E5DC',
                          boxShadow: `0px 0px 10px 0px #0000000A,
                            0px -0.5px 0.25px 0px #0000004D,
                            0px 0.5px 0.75px 0px #FFFFFF,
                            0px 2.07px 3.73px 0px #00000045 inset,
                            0px 1.08px 2.48px 0px #00000040 inset`,
                          borderRadius: '100px',
                          opacity: 1,
                        },
                        '& .MuiSlider-thumb': {
                          background: '#005CAD',
                          width: '24px',
                          height: '24px',
                          boxShadow: `
                          0px 0px 34.77px 0px #0000000D inset,
                          0px -0.5px 0.25px 0px #00000080 inset,
                          0px 0.5px 0.75px 0px #FFFFFF inset,
                          0px 2.07px 3.73px 0px #0000004D,
                          0px 2.08px 2.48px -2px #00000066,
                          0px 3.08px 2px -2px #00000054
                        `,
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054`,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Search on Map Button */}

                <ButtonTertiary
                  onClick={props.handleOpenMapPopup}
                  sx={{
                    '&&': {
                      width: '100%',
                      height: '48px',
                    },
                  }}
                >
                  <MapIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      mr: '4px',
                      '&& path': {
                        fill: props.isDark ? '#D5D5D5' : '#1A1A1A',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'Montserrat',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '32px',
                      color: props.isDark ? '#D5D5D5' : '#1A1A1A',
                    }}
                  >
                    Search on map
                  </Box>
                </ButtonTertiary>
              </Box>
            )}

            {/* Handyman Details Filter */}
            {props.isNameFilterVisible && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '24px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Montserrat',
                    fontStyle: 'normal',
                    fontWeight: 800,
                    fontSize: '20px',
                    lineHeight: '160%',
                    letterSpacing: '0.15px',
                    color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Handyman Details
                </Box>

                <FormControl fullWidth>
                  <GlassTextField
                    placeholder='Search Handyman by Name'
                    value={props.handymanSearchText}
                    onChange={props.handleHandymanSearchChange}
                    fullWidth
                    sx={{
                      '& input::placeholder': {
                        color: props.isDark ? '#B0B0B0' : '#1A1A1A',
                        opacity: 1,
                      },
                    }}
                  />
                </FormControl>

                {/* Hourly Rate Filter */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '143%',
                      letterSpacing: '0.17px',
                      color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    Hourly Rate (£)
                  </Box>

                  <Box sx={{ width: '100%', px: '12px' }}>
                    <Slider
                      value={props.hourlyRateRange}
                      onChange={props.handleHourlyRateRangeChange}
                      min={0}
                      max={1000}
                      valueLabelDisplay='auto'
                      sx={{
                        width: '100%',
                        height: '8px',
                        padding: '0px',
                        '& .MuiSlider-track': {
                          background: '#005CAD',
                          boxShadow: `
                            0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054
                          `,
                          borderRadius: '100px',
                          border: 'none',
                        },
                        '& .MuiSlider-rail': {
                          background: props.isDark ? '#1A1A1A' : '#E5E5DC',
                          boxShadow: `0px 0px 10px 0px #0000000A,
                            0px -0.5px 0.25px 0px #0000004D,
                            0px 0.5px 0.75px 0px #FFFFFF,
                            0px 2.07px 3.73px 0px #00000045 inset,
                            0px 1.08px 2.48px 0px #00000040 inset`,
                          borderRadius: '100px',
                          opacity: 1,
                        },
                        '& .MuiSlider-thumb': {
                          background: '#005CAD',
                          width: '24px',
                          height: '24px',
                          boxShadow: `
                          0px 0px 34.77px 0px #0000000D inset,
                          0px -0.5px 0.25px 0px #00000080 inset,
                          0px 0.5px 0.75px 0px #FFFFFF inset,
                          0px 2.07px 3.73px 0px #0000004D,
                          0px 2.08px 2.48px -2px #00000066,
                          0px 3.08px 2px -2px #00000054
                        `,
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054`,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Fixed Price Filter */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '143%',
                      letterSpacing: '0.17px',
                      color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    Fixed Price (£)
                  </Box>

                  <Box sx={{ width: '100%', px: '12px' }}>
                    <Slider
                      value={props.fixedPriceRange}
                      onChange={props.handleFixedPriceRangeChange}
                      min={0}
                      max={10000}
                      valueLabelDisplay='auto'
                      sx={{
                        width: '100%',
                        height: '8px',
                        padding: '0px',
                        '& .MuiSlider-track': {
                          background: '#005CAD',
                          boxShadow: `
                            0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054
                          `,
                          borderRadius: '100px',
                          border: 'none',
                        },
                        '& .MuiSlider-rail': {
                          background: props.isDark ? '#1A1A1A' : '#E5E5DC',
                          boxShadow: `0px 0px 10px 0px #0000000A,
                            0px -0.5px 0.25px 0px #0000004D,
                            0px 0.5px 0.75px 0px #FFFFFF,
                            0px 2.07px 3.73px 0px #00000045 inset,
                            0px 1.08px 2.48px 0px #00000040 inset`,
                          borderRadius: '100px',
                          opacity: 1,
                        },
                        '& .MuiSlider-thumb': {
                          background: '#005CAD',
                          width: '24px',
                          height: '24px',
                          boxShadow: `
                          0px 0px 34.77px 0px #0000000D inset,
                          0px -0.5px 0.25px 0px #00000080 inset,
                          0px 0.5px 0.75px 0px #FFFFFF inset,
                          0px 2.07px 3.73px 0px #0000004D,
                          0px 2.08px 2.48px -2px #00000066,
                          0px 3.08px 2px -2px #00000054
                        `,
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054`,
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Verified Only Checkbox */}
                {/* <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: '0px',
                    gap: '8px',
                  }}
                >
                  <Checkbox
                    checked={onlyVerified}
                    onChange={(e) => setOnlyVerified(e.target.checked)}
                    sx={{
                      padding: '0px',
                      width: '20px',
                      height: '20px',
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                        color: isDark ? '#D5D5D5' : '#585858',
                      },
                      '&.Mui-checked .MuiSvgIcon-root': {
                        color: '#005CAD',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '150%',
                      letterSpacing: '0.15px',
                      color: isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    Verified only
                  </Box>
                </Box> */}

                {/* Filter Buttons */}
                {props.isCategoriesFilterVisible && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      padding: '0px',
                      gap: '16px',
                    }}
                  >
                    {/* Reset Filters Button */}
                    <NeumorphicButton
                      startIcon={
                        <RefreshIcon
                          sx={{
                            width: '16px',
                            height: '16px',
                            color: '#E2F7FF',
                            ml: 0,
                          }}
                        />
                      }
                      onClick={props.handleResetFilters}
                      disabled={!props.hasActiveFilters}
                      sx={{
                        '&&': {
                          padding: '4px 12px',
                          height: '32px',
                          fontSize: '12px',
                          '&:disabled': {
                            background: GlassColors.buttonBackground,
                            color: '#777',
                          },
                          '&:disabled path': {
                            background: GlassColors.buttonBackground,
                            fill: '#777',
                          },
                        },
                      }}
                    >
                      Reset filters
                    </NeumorphicButton>

                    {/* Select All / Deselect All */}
                    {/* <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      padding: '0px',
                      gap: '16px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '4px 12px',
                        gap: '8px',
                        background: 'rgba(153, 153, 153, 0.3)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                        minHeight: '32px',
                        cursor:
                          props.isFetchingCategories ||
                          ALL_CATEGORY_KEYS.length === 0
                            ? 'not-allowed'
                            : 'pointer',
                        opacity:
                          props.isFetchingCategories ||
                          ALL_CATEGORY_KEYS.length === 0
                            ? 0.5
                            : 1,
                        '&:hover': {
                          background:
                            props.isFetchingCategories ||
                            ALL_CATEGORY_KEYS.length === 0
                              ? 'rgba(153, 153, 153, 0.3)'
                              : 'rgba(153, 153, 153, 0.4)',
                        },
                      }}
                      onClick={handleSelectAllCategories}
                    >
                      <Box
                        sx={{
                          fontFamily: 'Montserrat',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '24px',
                          color: isDark ? '#D5D5D5' : '#1A1A1A',
                        }}
                      >
                        Select all
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '4px 12px',
                        gap: '8px',
                        background: 'rgba(153, 153, 153, 0.3)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                        minHeight: '32px',
                        cursor: props.isFetchingCategories
                          ? 'not-allowed'
                          : 'pointer',
                        opacity: props.isFetchingCategories ? 0.5 : 1,
                        '&:hover': {
                          background: props.isFetchingCategories
                            ? 'rgba(153, 153, 153, 0.3)'
                            : 'rgba(153, 153, 153, 0.4)',
                        },
                      }}
                      onClick={handleDeselectAllCategories}
                    >
                      <Box
                        sx={{
                          fontFamily: 'Montserrat',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '24px',
                          color: isDark ? '#D5D5D5' : '#1A1A1A',
                        }}
                      >
                        Deselect all
                      </Box>
                    </Box>
                  </Box> */}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Drawer>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            margin: { xs: props.isMobile ? '24px 0 0 0' : 0, md: '0 16px' },
            gap: '32px',
          }}
        >
          {/* Filter Actions Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '0px',
              gap: '24px',
              width: '100%',
              position: 'relative',
            }}
          >
            {/* Title */}
            <Box
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: '133%',
                color: props.isDark ? '#00FFFF' : '#005CAD',
              }}
            >
              Filters
            </Box>

            {props.isMobile && (
              <ButtonTertiaryIcon
                onClick={() => props.setMobileFiltersOpen(false)}
                sx={{
                  position: 'absolute',
                  zIndex: 1,
                  width: '32px',
                  height: '32px',
                  top: 0,
                  right: 0,
                  '& svg': {
                    fontSize: '16px',
                    '& path': {
                      fill: '#E2F7FF',
                    },
                  },
                }}
              >
                <CloseIcon />
              </ButtonTertiaryIcon>
            )}
          </Box>

          {/* Categories Section */}
          {props.isCategoriesFilterVisible && (
            <>
              {props.isFetchingCategories && (
                // <Box
                //   sx={{
                //     display: 'flex',
                //     justifyContent: 'center',
                //     my: 2,
                //   }}
                // >
                //   <CircularProgress size={24} sx={{ color: '#00FFFF' }} />
                //   </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '16px',
                    gap: '24px',
                    borderRadius: '32px',
                    width: '100%',
                  }}
                >
                  {/* Title */}
                  <Skeleton
                    variant='text'
                    width={140}
                    height={32}
                    sx={{ borderRadius: '8px' }}
                  />

                  {/* Search */}
                  <Skeleton
                    variant='rounded'
                    width='100%'
                    height={48}
                    sx={{ borderRadius: '999px' }}
                  />

                  {/* Categories list */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      width: '100%',
                    }}
                  >
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: '100%',
                          padding: '8px 15px 8px 24px',
                          borderRadius: '32px',
                        }}
                      >
                        {/* Row skeleton (іконка + текст) */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                          }}
                        >
                          <Skeleton variant='circular' width={32} height={32} />
                          <Skeleton variant='text' width='60%' height={24} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {props.categoriesError && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      mb: 1,
                      fontFamily: 'Roboto',
                      fontSize: '14px',
                      color: '#F44336',
                    }}
                  >
                    {props.categoriesError}
                  </Typography>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => props.getAllCategories()}
                    sx={{ textTransform: 'none' }}
                  >
                    Retry
                  </Button>
                </Box>
              )}

              {!props.isFetchingCategories &&
                !props.categoriesError &&
                props.transformedCategories.length === 0 && (
                  <Typography
                    sx={{
                      mb: 2,
                      fontFamily: 'Roboto',
                      fontSize: '14px',
                      color: props.isDark ? '#D5D5D5' : '#585858',
                    }}
                  >
                    No categories available
                  </Typography>
                )}

              {props.transformedCategories.length > 0 && (
                <CategoryFilterNew
                  categories={props.transformedCategories}
                  selectedCategoryKeys={props.selectedCategoryKeys}
                  toggleCategory={props.toggleCategory}
                  addAllSubcategoryKeys={props.addAllSubcategoryKeys}
                  removeAllSubcategoryKeys={props.removeAllSubcategoryKeys}
                />
              )}
            </>
          )}

          {/* Location Filter */}
          {props.isLocationFilterVisible && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '24px',
                width: '100%',
                '& > div': {
                  width: '100%',
                },
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  fontWeight: 800,
                  fontSize: '20px',
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                  color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                }}
              >
                Location
              </Box>

              <Autocomplete
                onLoad={(autocomplete) => {
                  props.autocompleteRef.current = autocomplete;
                }}
                onPlaceChanged={() => {
                  const place = props.autocompleteRef.current?.getPlace();
                  if (place?.formatted_address) {
                    props.setAddressInputValue(place.formatted_address);
                    props.setSelectedAddress(place.formatted_address);
                    const lat = place.geometry?.location?.lat();
                    const lng = place.geometry?.location?.lng();
                    if (typeof lat === 'number' && typeof lng === 'number') {
                      props.setSelectedCoordinates({ lat, lng });
                    } else {
                      props.setSelectedCoordinates(null);
                    }
                  }
                }}
              >
                <GlassTextField
                  placeholder={`${props.selectedAddress || ''} Search Address`}
                  fullWidth
                  value={props.addressInputValue}
                  onChange={(e) => {
                    props.setAddressInputValue(e.target.value);
                    // Freeform typing invalidates previously-picked coordinates.
                    props.setSelectedCoordinates(null);
                  }}
                  sx={{
                    '& input::placeholder': {
                      color: props.isDark ? '#B0B0B0' : '#1A1A1A',
                      opacity: 1,
                    },
                  }}
                />
              </Autocomplete>

              {/* Use Order Address Button */}
              {props.address && (
                <Button
                  variant='outlined'
                  size='small'
                  onClick={props.handleUseOrderAddress}
                  sx={{
                    mt: 1,
                    width: '100%',
                    fontFamily: 'Montserrat',
                    fontSize: '12px',
                    textTransform: 'none',
                    borderColor: props.isDark
                      ? 'rgba(255, 255, 255, 0.3)'
                      : 'rgba(0, 0, 0, 0.3)',
                    color: props.isDark ? '#D5D5D5' : '#1A1A1A',
                    '&:hover': {
                      borderColor: props.isDark
                        ? 'rgba(255, 255, 255, 0.5)'
                        : 'rgba(0, 0, 0, 0.5)',
                      backgroundColor: props.isDark
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                >
                  Use Order Address
                </Button>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '16px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '143%',
                    letterSpacing: '0.17px',
                    color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Search Radius (miles)
                </Box>

                <Box sx={{ width: '100%', px: '12px' }}>
                  <Slider
                    value={props.radiusKm ?? 10}
                    onChange={(e, value) => props.setRadiusKm(value as number)}
                    onChangeCommitted={(e, value) => {
                      props.setAppliedRadiusKm(value as number);
                    }}
                    min={1}
                    max={200}
                    step={1}
                    valueLabelDisplay='auto'
                    sx={{
                      width: '100%',
                      height: '8px',
                      padding: '0px',
                      '& .MuiSlider-track': {
                        background: '#005CAD',
                        boxShadow: `
                        0px 0px 34.77px 0px #0000000D inset,
                        0px -0.5px 0.25px 0px #00000080 inset,
                        0px 0.5px 0.75px 0px #FFFFFF inset,
                        0px 2.07px 3.73px 0px #0000004D,
                        0px 2.08px 2.48px -2px #00000066,
                        0px 3.08px 2px -2px #00000054
                      `,
                        borderRadius: '100px',
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        background: props.isDark ? '#1A1A1A' : '#E5E5DC',
                        boxShadow: `0px 0px 10px 0px #0000000A,
                        0px -0.5px 0.25px 0px #0000004D,
                        0px 0.5px 0.75px 0px #FFFFFF,
                        0px 2.07px 3.73px 0px #00000045 inset,
                        0px 1.08px 2.48px 0px #00000040 inset`,
                        borderRadius: '100px',
                        opacity: 1,
                      },
                      '& .MuiSlider-thumb': {
                        background: '#005CAD',
                        width: '24px',
                        height: '24px',
                        boxShadow: `
                        0px 0px 34.77px 0px #0000000D inset,
                        0px -0.5px 0.25px 0px #00000080 inset,
                        0px 0.5px 0.75px 0px #FFFFFF inset,
                        0px 2.07px 3.73px 0px #0000004D,
                        0px 2.08px 2.48px -2px #00000066,
                        0px 3.08px 2px -2px #00000054
                      `,
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0px 0px 34.77px 0px #0000000D inset,
                          0px -0.5px 0.25px 0px #00000080 inset,
                          0px 0.5px 0.75px 0px #FFFFFF inset,
                          0px 2.07px 3.73px 0px #0000004D,
                          0px 2.08px 2.48px -2px #00000066,
                          0px 3.08px 2px -2px #00000054`,
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              <ButtonTertiary
                onClick={props.handleOpenMapPopup}
                sx={{
                  '&&': {
                    width: '100%',
                    height: '48px',
                  },
                }}
              >
                <MapIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    mr: '4px',
                    '&& path': {
                      fill: props.isDark ? '#D5D5D5' : '#1A1A1A',
                    },
                  }}
                />
                <Box
                  sx={{
                    fontFamily: 'Montserrat',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '32px',
                    color: props.isDark ? '#D5D5D5' : '#1A1A1A',
                  }}
                >
                  Search on map
                </Box>
              </ButtonTertiary>
            </Box>
          )}

          {/* Handyman Details Filter */}
          {props.isNameFilterVisible && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '24px',
                width: '100%',
                mb: '7px',
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  fontWeight: 800,
                  fontSize: '20px',
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                  color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                }}
              >
                Handyman Details
              </Box>

              <FormControl fullWidth>
                <GlassTextField
                  placeholder='Search Handyman by Name'
                  value={props.handymanSearchText}
                  onChange={props.handleHandymanSearchChange}
                  fullWidth
                  sx={{
                    '& input::placeholder': {
                      color: props.isDark ? '#B0B0B0' : '#1A1A1A',
                      opacity: 1,
                    },
                  }}
                />
              </FormControl>

              {/* Hourly Rate Filter */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Hourly Rate (£)
                </Box>

                <Box sx={{ width: '100%', px: '12px' }}>
                  <Slider
                    value={props.hourlyRateRange}
                    onChange={props.handleHourlyRateRangeChange}
                    min={0}
                    max={1000}
                    valueLabelDisplay='auto'
                    sx={{
                      width: '100%',
                      height: '8px',
                      padding: '0px',
                      '& .MuiSlider-track': {
                        background: '#005CAD',
                        boxShadow: `
                            0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054
                          `,
                        borderRadius: '100px',
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        background: props.isDark ? '#1A1A1A' : '#E5E5DC',
                        boxShadow: `0px 0px 10px 0px #0000000A,
                            0px -0.5px 0.25px 0px #0000004D,
                            0px 0.5px 0.75px 0px #FFFFFF,
                            0px 2.07px 3.73px 0px #00000045 inset,
                            0px 1.08px 2.48px 0px #00000040 inset`,
                        borderRadius: '100px',
                        opacity: 1,
                      },
                      '& .MuiSlider-thumb': {
                        background: '#005CAD',
                        width: '24px',
                        height: '24px',
                        boxShadow: `
                          0px 0px 34.77px 0px #0000000D inset,
                          0px -0.5px 0.25px 0px #00000080 inset,
                          0px 0.5px 0.75px 0px #FFFFFF inset,
                          0px 2.07px 3.73px 0px #0000004D,
                          0px 2.08px 2.48px -2px #00000066,
                          0px 3.08px 2px -2px #00000054
                        `,
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054`,
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Fixed Price Filter */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    color: props.isDark ? '#B6B6B6' : '#1A1A1A',
                  }}
                >
                  Fixed Price (£)
                </Box>

                <Box sx={{ width: '100%', px: '12px' }}>
                  <Slider
                    value={props.fixedPriceRange}
                    onChange={props.handleFixedPriceRangeChange}
                    min={0}
                    max={10000}
                    valueLabelDisplay='auto'
                    sx={{
                      width: '100%',
                      height: '8px',
                      padding: '0px',
                      '& .MuiSlider-track': {
                        background: '#005CAD',
                        boxShadow: `
                            0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054
                          `,
                        borderRadius: '100px',
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        background: props.isDark ? '#1A1A1A' : '#E5E5DC',
                        boxShadow: `0px 0px 10px 0px #0000000A,
                            0px -0.5px 0.25px 0px #0000004D,
                            0px 0.5px 0.75px 0px #FFFFFF,
                            0px 2.07px 3.73px 0px #00000045 inset,
                            0px 1.08px 2.48px 0px #00000040 inset`,
                        borderRadius: '100px',
                        opacity: 1,
                      },
                      '& .MuiSlider-thumb': {
                        background: '#005CAD',
                        width: '24px',
                        height: '24px',
                        boxShadow: `
                          0px 0px 34.77px 0px #0000000D inset,
                          0px -0.5px 0.25px 0px #00000080 inset,
                          0px 0.5px 0.75px 0px #FFFFFF inset,
                          0px 2.07px 3.73px 0px #0000004D,
                          0px 2.08px 2.48px -2px #00000066,
                          0px 3.08px 2px -2px #00000054
                        `,
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054`,
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Filter Buttons */}
              {props.isCategoriesFilterVisible && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0px',
                    gap: '16px',
                  }}
                >
                  <NeumorphicButton
                    startIcon={
                      <RefreshIcon
                        sx={{
                          width: '16px',
                          height: '16px',
                          color: '#E2F7FF',
                          ml: 0,
                        }}
                      />
                    }
                    onClick={props.handleResetFilters}
                    disabled={!props.hasActiveFilters}
                    sx={{
                      '&&': {
                        padding: '4px 12px',
                        height: '32px',
                        fontSize: '12px',
                        '&:disabled': {
                          background: GlassColors.buttonBackground,
                          color: '#777',
                        },
                        '&:disabled path': {
                          background: GlassColors.buttonBackground,
                          fill: '#777',
                        },
                      },
                    }}
                  >
                    Reset filters
                  </NeumorphicButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
